import { formatForDisplay } from "@tanstack/react-hotkeys";

import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { Search } from "@/shared/foundation/icons";
import { Kbd } from "@/shared/foundation/primitives/Hotkey";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";

import { QUICK_SEARCH_HOTKEY } from "./useQuickSearchHotkey";

export interface QuickSearchButtonProps {
  onClick: () => void;
  /**
   * Resource-specific tooltip and accessible name, e.g. `"Find dataset(s)"`.
   * Names what the palette searches, since one toolbar's palette differs from
   * the next's.
   */
  label: string;
}

/**
 * Icon-only toolbar trigger for the quick-search palette. The tooltip is the
 * discoverable surface for `useQuickSearchHotkey`'s otherwise-invisible
 * binding. Deliberately compact so it reads as a utility control and doesn't
 * compete with the Filter button, the primary path for complex filters.
 *
 * The `<Kbd>` derives from `QUICK_SEARCH_HOTKEY`, so it resolves to the
 * viewer's platform (`⌘K` / `Ctrl+K`) and cannot drift from the binding.
 *
 * The tooltip is hover-only (`disableFocusListener`): the palette restores
 * focus to this button on close, and MUI treats that keyboard-driven focus as
 * focus-visible — which would pop the tooltip right after every keyboard
 * dismissal. Keyboard and screen-reader users get the name and shortcut from
 * `aria-label` instead, so nothing is lost by not opening the tooltip on focus.
 */
export function QuickSearchButton({ onClick, label }: QuickSearchButtonProps) {
  return (
    <RobotoTooltip
      disableFocusListener
      title={
        <span className="flex items-center gap-2xs">
          {label}
          <Kbd hotkey={QUICK_SEARCH_HOTKEY} />
        </span>
      }
    >
      <RobotoButton
        variant="secondary"
        icon={<Search aria-hidden="true" />}
        onClick={onClick}
        // The Kbd inside the tooltip is aria-hidden, so the shortcut is
        // spelled out here for screen readers.
        aria-label={`${label} (${formatForDisplay(QUICK_SEARCH_HOTKEY)})`}
        aria-haspopup="dialog"
      />
    </RobotoTooltip>
  );
}
